/**
 * Client-side analysis boundary for crest extraction — the twin of
 * `scripts/boundary.py`.
 *
 * DEVIATION / SURFACE_DIFFERENCE / GROUND_CLASSIFY crop their analysis to a
 * vector layer's XY footprint on the SERVER (`scripts/boundary.py`, fed a
 * `boundary.geojson` written by preprocess). Crest extraction runs entirely in
 * the browser, so the same rule has to be re-implemented here — which makes
 * this one of the client/server twins CLAUDE.md warns about. The parse rules
 * below are a deliberate, line-for-line match of `boundary.py`; change one
 * side and you must change the other, or the same boundary layer will crop a
 * deviation run and a crest run differently with nothing failing:
 *
 *   boundary.py `_geometry_polygons`      | here `geometryPolygons`
 *   ------------------------------------- | ---------------------------------
 *   Polygon        -> rings[0] + holes     | same
 *   MultiPolygon   -> one polygon per part | same
 *   LineString     -> only if CLOSED       | same
 *   MultiLineString-> only closed parts    | same
 *   GeometryCollection -> recurse          | same
 *   ring needs >= 4 coords                 | same (`MIN_RING`)
 *   closed test: |first - last| < 1e-9     | same (`EPS`)
 *   Z ignored everywhere                   | same
 *   inside = in outer AND in no hole       | same
 *   per-polygon bbox pre-filter            | same (`polygonAabb`)
 *
 * The one intentional difference is the point-in-polygon kernel: the server
 * uses matplotlib's `Path.contains_points`, we use the repo's existing
 * `pointInPolygon2D` ray-cast (`@/lib/stockpile/pointInPolygon`). Both are
 * even-odd rules; they can only disagree for a point sitting exactly ON an
 * edge, which for a station pick is a measure-zero case.
 *
 * THE TWIN IS THREE-WAY, not two. `scripts/boundary.py` never sees the
 * database: `libs/database/src/boundaryGeometry.ts`
 * (`featuresToBoundaryCollection`) is the middle hop that turns feature ROWS
 * into the `boundary.geojson` FeatureCollection preprocess uploads, applying
 * the same closure / MIN_RING / Z-stripping rules a third time. So a rule
 * change has to land in all three files:
 *
 *   this file  <->  libs/database/src/boundaryGeometry.ts  <->  scripts/boundary.py
 *
 * One divergence is deliberate and must NOT be mirrored here:
 * `boundaryGeometry.ts` reads `row.geometry ?? row.geometryWGS84`, falling
 * back to the WGS84 column when the projected one is absent. This file reads
 * only the projected `geometry` a feature carries, because crest extraction
 * clips in the sampler's own projected frame, where degrees would be
 * nonsense. That fallback is out of scope here and may itself be a latent bug
 * on the server side (it can feed WGS84 degrees to a projected-frame worker);
 * it is recorded, not copied.
 */
import { pointInPolygon2D, polygonAabb } from '@/lib/stockpile/pointInPolygon';

/** `boundary.py`'s `_EPS`. */
const EPS = 1e-9;
/** `boundary.py`'s `len(rings[0]) < 4` / `len(coords) < 4` guards. */
const MIN_RING = 4;

/** Human-readable twin of `boundary.py`'s `EMPTY_MESSAGE` condition. */
export interface BoundaryPolygon {
  outer: Array<[number, number]>;
  holes: Array<Array<[number, number]>>;
  aabb: { minX: number; minY: number; maxX: number; maxY: number };
}

type Ring = ReadonlyArray<ReadonlyArray<number>>;

function ringXY(coords: Ring): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const c of coords) out.push([Number(c[0]), Number(c[1])]);
  return out;
}

/** `boundary.py::_is_closed` — first vertex equals last, at least 4 vertices. */
function isClosed(coords: Ring): boolean {
  if (coords.length < MIN_RING) return false;
  const a = coords[0];
  const b = coords[coords.length - 1];
  return Math.abs(Number(a[0]) - Number(b[0])) < EPS && Math.abs(Number(a[1]) - Number(b[1])) < EPS;
}

function polygonFromRings(rings: readonly Ring[]): BoundaryPolygon | null {
  if (!rings || rings.length === 0 || rings[0].length < MIN_RING) return null;
  const outer = ringXY(rings[0]);
  const holes = rings.slice(1).filter((r) => r.length >= MIN_RING).map(ringXY);
  return { outer, holes, aabb: polygonAabb(outer) };
}

/** `boundary.py::_geometry_polygons`. */
export function geometryPolygons(geom: unknown): BoundaryPolygon[] {
  if (!geom || typeof geom !== 'object') return [];
  const g = geom as { type?: string; coordinates?: unknown; geometries?: unknown[] };
  const coords = g.coordinates as any;
  switch (g.type) {
    case 'Polygon': {
      const p = polygonFromRings(coords as Ring[]);
      return p ? [p] : [];
    }
    case 'MultiPolygon':
      return ((coords as Ring[][]) ?? [])
        .map((rings) => polygonFromRings(rings))
        .filter((p): p is BoundaryPolygon => p !== null);
    case 'LineString': {
      const ring = (coords as Ring) ?? [];
      if (!isClosed(ring)) return [];
      const outer = ringXY(ring);
      return [{ outer, holes: [], aabb: polygonAabb(outer) }];
    }
    case 'MultiLineString':
      return ((coords as Ring[]) ?? []).filter(isClosed).map((part) => {
        const outer = ringXY(part);
        return { outer, holes: [], aabb: polygonAabb(outer) };
      });
    case 'GeometryCollection':
      return (g.geometries ?? []).flatMap((child) => geometryPolygons(child));
    default:
      return [];
  }
}

/**
 * Every usable polygon carried by a vector layer's features.
 *
 * `boundary.py` receives a FeatureCollection and walks `features[].geometry`;
 * the client already has the features individually (the `['features', layerId]`
 * react-query cache the viewer fills), so this takes the array directly.
 * An empty result is `boundary.py`'s `EMPTY_MESSAGE` case — there, a raised
 * `ValueError`; here, the caller shows a note and runs unbounded.
 */
export function boundaryPolygonsFromFeatures(features: ReadonlyArray<{ geometry?: unknown }>): BoundaryPolygon[] {
  return features.flatMap((f) => geometryPolygons(f?.geometry));
}

/**
 * A point-in-boundary predicate, or `null` when there is nothing to test
 * against (no polygons) — `null` means "no constraint", never "reject
 * everything".
 *
 * Mirrors `boundary.py::boundary_mask`: a point is inside when it falls in
 * SOME polygon's outer ring and in none of THAT polygon's holes. The AABB
 * pre-cull is the same optimisation the server makes, and it matters here for
 * the same reason — this runs once per station, for every parameter change,
 * on the UI thread.
 */
export function makeInsideBoundary(polys: ReadonlyArray<BoundaryPolygon>): ((x: number, y: number) => boolean) | null {
  if (polys.length === 0) return null;
  return (x: number, y: number): boolean => {
    for (const poly of polys) {
      const { minX, minY, maxX, maxY } = poly.aabb;
      if (x < minX || x > maxX || y < minY || y > maxY) continue;
      if (!pointInPolygon2D(x, y, poly.outer)) continue;
      let inHole = false;
      for (const hole of poly.holes) {
        if (pointInPolygon2D(x, y, hole)) {
          inHole = true;
          break;
        }
      }
      if (!inHole) return true;
    }
    return false;
  };
}
